import React, { useEffect, useContext, useState } from 'react';
import { AppContext, ModalContext, formatListData, AjaxContext } from 'commons-ui';
import { GAME_NAMES, GAME_NAME_OPTIONS } from 'pages/constants';
import { MISCELLANEOUS_RESOURCES, PAGE_NAMES } from 'constant';

import { SelectFilterContent } from '../submodules/selectFilter';
import { CreditContent } from '../submodules/credit';
import { IkindRewardFilterData, IListUserKindRewardPayload, RESPONSE, IKindRewardCreditPayload } from '../kindReward.model';

const useService = () => {
	const {
		showAlert,
		app: { appName }
	} = useContext(AppContext);
	const { postData } = useContext(AjaxContext);
	const { setModalConfig } = useContext(ModalContext);
	const [showLoader, setShowLoader] = useState<boolean>(false);
	const [kindRewardData, serKindRewardData] = useState<{ data: IListUserKindRewardPayload[]; rowCount: number }>({
		data: [],
		rowCount: 0
	});
	const [filters, setFilters] = useState<IkindRewardFilterData | undefined>(undefined);
	const [forceUpdate, setForceUpdate] = useState<boolean>(false);

	const handleFilterFormClose = () => {
		setModalConfig({ isOpen: false });
	};

	const getUserKindRewards = async (): Promise<void> => {
		try {
			setShowLoader(true);
			const filterData: IkindRewardFilterData = filters as IkindRewardFilterData;
			const response: RESPONSE<{ data: IListUserKindRewardPayload[] }> = await postData({
				endpoint: `/dashboard/fetchUserKindReward/${PAGE_NAMES.miscellaneous}-${MISCELLANEOUS_RESOURCES.kindRewards}-fetchUserKindReward`,
				queryParams: {
					appName
				},
				body: {
					zupeeUserId: Number(filterData?.userId),
					ltid: Number(filterData?.tournamentID),
					gameName: GAME_NAME_OPTIONS[GAME_NAMES.LUDONX]
				}
			});
			const kindRewardResponse: IListUserKindRewardPayload[] = response?.data?.data as IListUserKindRewardPayload[];
			serKindRewardData({ rowCount: kindRewardResponse.length, data: formatListData(kindRewardResponse) });
			setForceUpdate(true);
		} catch (error: any) {
			serKindRewardData({ rowCount: 0, data: [] });
			setForceUpdate(true);
			showAlert('Error while getting user kind reward data!', { type: 'error' });
		} finally {
			setShowLoader(false);
		}
	};

	const creditKindRewardToTheUser = async (
		kindRewardCreditPayload: IKindRewardCreditPayload,
		rowData: IListUserKindRewardPayload
	): Promise<void> => {
		try {
			const response: RESPONSE<{ success: boolean }> = await postData({
				endpoint: `/dashboard/creditKindRewards/${PAGE_NAMES.miscellaneous}-${MISCELLANEOUS_RESOURCES.kindRewards}-creditKindRewards`,
				queryParams: {
					appName
				},
				body: {
					registrationId: rowData?.registrationId,
					ltid: Number(rowData?.ltid),
					zupeeUserId: Number(rowData?.uid),
					creditType: kindRewardCreditPayload.creditType,
					buyingPrice: kindRewardCreditPayload.buyingPrice,
					isTDSEligible: true,
					gameName: GAME_NAME_OPTIONS[GAME_NAMES.LUDONX]
				}
			});
			if (!response.data?.success) throw Error(response.externalMessage);
			showAlert('Kind reward credited!', { type: 'success' });
			setForceUpdate(false);
		} catch (error: any) {
			setForceUpdate(true);
			showAlert('Error in crediting kind reward to the user or it is already credited!', { type: 'error' });
		}
	};

	const handleFilterSubmit = (submittedFilters: IkindRewardFilterData) => {
		const { tournamentID, userId } = submittedFilters;
		setFilters({ userId, tournamentID });
	};

	const handleFilterModalOpen = () => {
		setModalConfig({
			isOpen: true,
			header: 'Kind Reward Filter',
			content: (
				<SelectFilterContent
					handleFilterSubmit={handleFilterSubmit}
					onClose={handleFilterFormClose}
					handleRewardCredit={() => {}}
				/>
			)
		});
	};

	const handleRewardModalOpen = (rowData: IListUserKindRewardPayload): void => {
		setModalConfig({
			isOpen: true,
			header: 'Credit Reward(Kind/Winning)',
			content: (
				<CreditContent
					handleRewardCredit={creditKindRewardToTheUser}
					onClose={handleFilterFormClose}
					handleFilterSubmit={() => {}}
					rowData={rowData}
				/>
			)
		});
	};

	const getAllKindRewardData = () => {
		setForceUpdate(false);
		return kindRewardData;
	};

	useEffect(() => {
		handleFilterModalOpen();
	}, []);

	useEffect(() => {
		if (filters !== null && filters !== undefined) {
			getUserKindRewards();
		}
	}, [filters]);

	return {
		getAllKindRewardData,
		filters,
		showLoader,
		forceUpdate,
		setShowLoader,
		handleFilterFormClose,
		handleFilterSubmit,
		handleFilterModalOpen,
		handleRewardModalOpen,
		creditKindRewardToTheUser
	};
};

export default useService;
